import {
  estimateMonthlyCost,
  formatCurrency,
  provisionPersistentDisk,
} from "@app/deploy";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useSelector,
} from "@app/react";
import { environmentActivityUrl } from "@app/routes";
import { schema } from "@app/schema";
import { useValidator } from "@app/ui/hooks";
import { BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { Form, FormGroup } from "@app/ui/shared/form-group";
import { IconCylinder } from "@app/ui/shared/icons";
import { Input } from "@app/ui/shared/input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { handleValidator } from "@app/validator";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface PersistentDiskFormData {
  handle: string;
  size: number;
}

const persistentDiskValidators = {
  handle: (data: PersistentDiskFormData) => handleValidator(data.handle),
  size: (data: PersistentDiskFormData) => {
    if (!Number.isFinite(data.size) || data.size <= 0) {
      return "Size must be a positive number of GB";
    }
  },
};

interface CreatePersistentDiskModalProps {
  isOpen: boolean;
  envId: string;
  onClose: () => void;
}

export const CreatePersistentDiskModal = ({
  isOpen,
  envId,
  onClose,
}: CreatePersistentDiskModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [handle, setHandle] = useState("");
  // Size is stored as a raw string so the user can clear the field without it
  // reverting to 0; the validator surfaces an error for blank/invalid input.
  const [sizeInput, setSizeInput] = useState("10");
  const size = sizeInput.trim() === "" ? Number.NaN : Number(sizeInput);
  const [errors, validate] = useValidator<
    PersistentDiskFormData,
    typeof persistentDiskValidators
  >(persistentDiskValidators);

  // useLoader(creator) watches the NAME-keyed loader; the thunk publishes
  // start/success/error under its name via thunkLoader, so useLoaderSuccess
  // below fires (and the modal closes) once the saga succeeds.
  const loader = useLoader(provisionPersistentDisk);

  // R7a: estimated monthly cost, updating live with the size input. The rate
  // (persistent_disk_cost_gb_per_month, ~$0.20/GB/mo) comes from the
  // cost-rates module, not a hardcoded constant. provisionedIops:0 keeps this
  // to the GB term only. Falls back to 0 GB while the user is editing the
  // field so the line stays present rather than flashing NaN.
  const rates = useSelector(schema.costRates.select);
  const estCost = estimateMonthlyCost({
    rates,
    persistentDisks: [
      { size: Number.isFinite(size) ? size : 0, provisionedIops: 0 },
    ],
  });

  const handleClose = () => {
    setHandle("");
    setSizeInput("10");
    onClose();
  };

  useLoaderSuccess(loader, () => {
    navigate(environmentActivityUrl(envId));
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const data: PersistentDiskFormData = { handle, size };
    if (!validate(data)) return;
    dispatch(provisionPersistentDisk({ envId, handle, size }));
  };

  if (!isOpen) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={handleClose} className="min-w-[600px]">
        <ModalHeading
          icon={<IconCylinder variant="sm" />}
          title="Create Persistent Disk"
          onClose={handleClose}
        />
        <Form onSubmit={handleSubmit}>
          <ModalBody className="pb-0">
            <FormGroup
              label={
                <>
                  Handle <span className="text-red-500">*</span>
                </>
              }
              htmlFor="persistent-disk-handle"
              className="mb-4"
              feedbackMessage={errors.handle}
              feedbackVariant={errors.handle ? "danger" : "default"}
            >
              <Input
                id="persistent-disk-handle"
                name="handle"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="e.g., shared-uploads"
                maxLength={64}
              />
            </FormGroup>

            <FormGroup
              label={
                <>
                  Size (GB) <span className="text-red-500">*</span>
                </>
              }
              htmlFor="persistent-disk-size"
              className="mb-4"
              feedbackMessage={errors.size}
              feedbackVariant={errors.size ? "danger" : "default"}
            >
              <Input
                id="persistent-disk-size"
                name="size"
                type="number"
                min={1}
                value={sizeInput}
                onChange={(e) => setSizeInput(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                You can increase disk size at any time, but cannot decrease it.
              </p>
            </FormGroup>

            <p className="text-gray-500 text-sm">
              Est. cost: {formatCurrency(estCost)}/month
            </p>

            <BannerMessages className="mt-4" {...loader} />
          </ModalBody>
          <ModalFooter
            actions={[
              <Button
                key="submit"
                type="submit"
                disabled={!handle.trim() || !Number.isFinite(size) || size <= 0}
                isLoading={loader.isLoading}
              >
                Create Persistent Disk
              </Button>,
            ]}
          />
        </Form>
      </Modal>
    </ModalWrapper>
  );
};
